import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'react-toastify';
import ReactApexChart from 'react-apexcharts';
import ApexCharts from 'apexcharts';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Grid, TrendingUp } from 'react-feather';
import config from '../../../../../config';

import ReportShell from '../../shared/ReportShell';
import { formatMoney } from '../../shared/ReportTable';
import MultiSelect from '../../shared/MultiSelect';
import useReportLookups from '../../shared/useReportLookups';
import useReportAccess from '../../shared/useReportAccess';

const DATE_FILTERS = [
    { label: 'Previous day', value: 'previous_day' },
    { label: 'This week', value: 'this_week' },
    { label: 'Previous week', value: 'previous_week' },
    { label: 'This month', value: 'this_month' },
    { label: 'Previous month', value: 'previous_month' },
];

// x-axis categories come from the API response (daily date labels).

const cssVar = (name, fallback) => {
    if (typeof window === 'undefined') return fallback;
    const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    return v || fallback;
};

const initialFilters = {
    property_id: '',
    category_id: '',
    subcategory_id: '',
    group_name: '',
    selectedTenants: [],
};

const TimeSeriesTrendChartView = () => {
    const { t } = useTranslation();
    const allowed = useReportAccess();

    const login_name = localStorage.getItem('loginName');
    const login_id = localStorage.getItem('loginId');
    const token = localStorage.getItem('sessiontoken');
    const headers = useMemo(() => ({ Authorization: `${token}` }), [token]);

    const [filters, setFilters] = useState(initialFilters);
    const [activeDateFilter, setActiveDateFilter] = useState('previous_day');
    const [chartData, setChartData] = useState([]);
    const [categories, setCategories] = useState([]);
    const [totalNetSales, setTotalNetSales] = useState(0);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const lookups = useReportLookups({
        propertyId: filters.property_id,
        categoryId: filters.category_id,
        subcategoryId: filters.subcategory_id,
        groupName: filters.group_name,
    });
    const { concessionaireId } = lookups;

    const nameOf = (opts, val) => opts.find((o) => o.value === val)?.label || '';

    const insertToLog = (task) => {
        axios.post(`${config.hostname}/log`, {
            created_by: login_id,
            concessionaire_id: concessionaireId,
            module: 'Reports',
            task: `${login_name}${task}`,
        }, { headers }).catch((err) => console.warn('audit log failed', err?.response?.status));
    };

    const fetchChartData = async (dateFilter) => {
        setLoading(true);
        setError('');
        try {
            const res = await axios.post(`${config.hostname}/reports/getTimeSeriesTrendChart`, {
                concessionaire_id: concessionaireId,
                date_filter: dateFilter,
                group_name: filters.group_name,
                category_name: nameOf(lookups.categoryOptions, filters.category_id),
                property_name: nameOf(lookups.propertyOptions, filters.property_id),
                subcategory_name: nameOf(lookups.subcategoryOptions, filters.subcategory_id),
                tenant_id: filters.selectedTenants,
            }, { headers });
            setChartData(res.data?.lineOptions?.netSales?.data || []);
            setCategories(res.data?.lineOptions?.categories || res.data?.lineOptions?.Hour?.Hour || []);
            setTotalNetSales(res.data?.totalNetSales || 0);
            setStartDate(res.data?.start_date || '');
            setEndDate(res.data?.end_date || '');
        } catch (err) {
            console.error('time series report failed', err);
            setError(err?.response?.data?.message || t('Could not load the trend for the selected period.'));
            toast.error(t('Failed to load trend chart'));
        } finally {
            setLoading(false);
        }
    };

    // Auto-fetch when filters or the active preset change.
    useEffect(() => {
        if (!concessionaireId) return;
        fetchChartData(activeDateFilter);
    }, [concessionaireId, activeDateFilter, filters.property_id, filters.category_id, filters.subcategory_id, filters.group_name, filters.selectedTenants]);

    const onFilterChange = (key, value) => {
        setFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === 'category_id') { next.subcategory_id = ''; next.selectedTenants = []; }
            if (key === 'property_id') { next.selectedFloor = ''; next.selectedTenants = []; }
            if (key === 'subcategory_id') { next.selectedTenants = []; }
            if (key === 'group_name') { next.selectedTenants = []; }
            return next;
        });
    };

    // ── Themed chart options ────────────────────────────────────────
    const chartOptions = useMemo(() => {
        const accent = cssVar('--v3-accent', '#3B82F6');
        const teal = cssVar('--v3-teal', '#06B6D4');
        const grid = cssVar('--v3-chart-grid', 'rgba(255,255,255,0.04)');
        const textSecondary = cssVar('--v3-text-secondary', '#94A3B8');
        const textMuted = cssVar('--v3-text-muted', '#64748B');
        return {
            series: [{ name: t('Net Sales'), data: chartData }],
            chart: { id: 'netSalesChart', type: 'area', height: 350, toolbar: { show: false }, background: 'transparent', fontFamily: 'inherit' },
            theme: { mode: 'dark' },
            stroke: { curve: 'smooth', width: 3 },
            colors: [accent],
            fill: {
                type: 'gradient',
                gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100], colorStops: [
                    { offset: 0, color: accent, opacity: 0.35 },
                    { offset: 100, color: teal, opacity: 0.02 },
                ] },
            },
            grid: { borderColor: grid, strokeDashArray: 4 },
            dataLabels: { enabled: false },
            xaxis: {
                categories,
                title: { text: t('Date'), style: { color: textMuted, fontWeight: 500 } },
                labels: { style: { colors: textSecondary } },
                axisBorder: { color: grid },
                axisTicks: { color: grid },
                tooltip: { enabled: false },
            },
            yaxis: {
                title: { text: t('Net Sales'), style: { color: textMuted, fontWeight: 500 } },
                labels: { style: { colors: textSecondary }, formatter: (v) => Number(v).toLocaleString() },
            },
            tooltip: {
                theme: 'dark',
                x: { formatter: (val) => String(val) },
                y: { formatter: (val) => Number(val).toFixed(2) },
            },
        };
    }, [chartData, categories, t]);

    const exportChartToExcel = async () => {
        try {
            const chartResult = await ApexCharts.exec('netSalesChart', 'dataURI');
            if (!chartResult || !chartResult.imgURI) { toast.error(t('Chart not ready yet')); return; }
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Net Sales Trend');
            worksheet.mergeCells('A1:J1');
            worksheet.getCell('A1').value = t('Net Sales Trend Report');
            worksheet.getCell('A1').font = { size: 16, bold: true };
            worksheet.addRow([]);
            worksheet.addRow([t('Period'), `${startDate} – ${endDate}`]);
            worksheet.addRow([t('Total Net Sales'), Number(totalNetSales).toFixed(2)]);
            worksheet.addRow([t('Property'), nameOf(lookups.propertyOptions, filters.property_id) || t('All')]);
            worksheet.addRow([t('Category'), nameOf(lookups.categoryOptions, filters.category_id) || t('All')]);
            worksheet.addRow([t('Subcategory'), nameOf(lookups.subcategoryOptions, filters.subcategory_id) || t('All')]);
            worksheet.addRow([t('Group'), filters.group_name || t('All')]);
            worksheet.addRow([t('Exported By'), login_name]);
            worksheet.addRow([t('Exported On'), new Date().toLocaleString()]);
            worksheet.addRow([]);
            const imageId = workbook.addImage({ base64: chartResult.imgURI, extension: 'png' });
            worksheet.addImage(imageId, { tl: { col: 0, row: 12 }, ext: { width: 900, height: 420 } });
            const buffer = await workbook.xlsx.writeBuffer();
            saveAs(new Blob([buffer]), `Net_Sales_Trend_${activeDateFilter}.xlsx`);
            insertToLog(' exported Time Series trend to excel');
        } catch (err) {
            console.error('chart export failed', err);
            toast.error(t('Failed to export chart'));
        }
    };

    if (!allowed) return null;

    const hasData = chartData.some((v) => Number(v) > 0);

    return (
        <Fragment>
            <ReportShell
                title={t('Time Series Trend')}
                subtitle={t('Daily net-sales trend across a chosen period.')}
                actions={
                    <button type="button" className="v3-btn v3-btn-ghost" onClick={exportChartToExcel} disabled={loading || !hasData} title={t('Export chart to Excel')}>
                        <Grid size={14} /> {t('Excel')}
                    </button>
                }
            >
                {/* Filters (auto-apply, no Generate) */}
                <div className="v3-card v3-report-filters">
                    <div className="v3-sadmin-form-grid-3">
                        <div className="v3-report-field">
                            <label className="v3-label" htmlFor="ts-property">{t('Property')}</label>
                            <div className="v3-select-wrap">
                                <select id="ts-property" className="v3-input v3-select" value={filters.property_id} onChange={(e) => onFilterChange('property_id', e.target.value)}>
                                    <option value="">{t('All properties')}</option>
                                    {lookups.propertyOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="v3-report-field">
                            <label className="v3-label" htmlFor="ts-category">{t('Category')}</label>
                            <div className="v3-select-wrap">
                                <select id="ts-category" className="v3-input v3-select" value={filters.category_id} onChange={(e) => onFilterChange('category_id', e.target.value)}>
                                    <option value="">{t('All categories')}</option>
                                    {lookups.categoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="v3-report-field">
                            <label className="v3-label" htmlFor="ts-subcategory">{t('Sub category')}</label>
                            <div className="v3-select-wrap">
                                <select id="ts-subcategory" className="v3-input v3-select" value={filters.subcategory_id} onChange={(e) => onFilterChange('subcategory_id', e.target.value)}>
                                    <option value="">{t('All sub categories')}</option>
                                    {lookups.subcategoryOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="v3-report-field">
                            <label className="v3-label" htmlFor="ts-group">{t('Group')}</label>
                            <div className="v3-select-wrap">
                                <select id="ts-group" className="v3-input v3-select" value={filters.group_name} onChange={(e) => onFilterChange('group_name', e.target.value)}>
                                    <option value="">{t('All groups')}</option>
                                    {lookups.groupOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="v3-report-field" style={{ gridColumn: 'span 2' }}>
                            <span className="v3-label">{t('Tenants')}</span>
                            <MultiSelect
                                value={filters.selectedTenants}
                                options={lookups.tenantOptions}
                                placeholder={t('All tenants')}
                                onChange={(next) => onFilterChange('selectedTenants', next)}
                            />
                        </div>
                    </div>

                    {/* Preset period segmented control */}
                    <div className="v3-report-filter-actions">
                        {DATE_FILTERS.map((btn) => (
                            <button
                                key={btn.value}
                                type="button"
                                className={`v3-btn ${activeDateFilter === btn.value ? 'v3-btn-primary' : 'v3-btn-ghost'}`}
                                onClick={() => setActiveDateFilter(btn.value)}
                            >
                                {t(btn.label)}
                            </button>
                        ))}
                    </div>
                </div>

                {/* KPI + chart */}
                <div className="v3-sadmin-kpis">
                    <div className="v3-card v3-sadmin-kpi">
                        <div className="v3-sadmin-kpi-top">
                            <span className="v3-label">{t('Total net sales')}</span>
                            <span className="v3-sadmin-kpi-icon accent"><TrendingUp size={16} /></span>
                        </div>
                        <span className="v3-sadmin-kpi-value">{formatMoney(totalNetSales)}</span>
                        <span className="v3-body" style={{ margin: 0 }}>{t('Period')}: {startDate || '—'} – {endDate || '—'}</span>
                    </div>
                </div>

                <div className="v3-card v3-report-chart-card">
                    <h3 className="v3-heading">{t('Net sales by day')}</h3>
                    {error ? (
                        <div className="v3-report-state error">
                            <h3>{t('Couldn’t load the chart')}</h3>
                            <p>{error}</p>
                        </div>
                    ) : (
                        <ReactApexChart options={chartOptions} series={chartOptions.series} type="area" height={350} />
                    )}
                </div>
            </ReportShell>
        </Fragment>
    );
};

export default TimeSeriesTrendChartView;
